import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { genAttributes, advance } from '@onward-delivery/core';
import { toNational, toE164 } from '@/utilities/formatPhoneNumber';
import { PICKUP_TYPES } from '@/components/ShipmentForm/constants/dropoffOptions';
import { Header1, Body1, Tag, Step, SubStepDesktop, SubStepMobile, Anchor } from './blocks';
import Dropoff from './Dropoff';
import OutForService from './OutForService';
import { ORIGIN_PICKUP, WAREHOUSE_LOAD } from './itemEvents';
import { formatInTimeZone } from 'date-fns-tz';
import { formatCalendarDate } from './utils';

function Pickup({
    isDesktop,
    isReceived,
    isLoading,
    warehouse,
    before,
    after,
    order,
    callbacks,
    tz,
    readOnly,
    lastEvent,
}) {
    const { full_address, location, location_type } = genAttributes(order, true);

    const STEPS = useMemo(() => {
        return [
            {
                description: `1. ${isReceived ? 'Load' : 'Pickup'} Items`,
                complete: ({ order, after }) => {
                    return !!after && !!order.loading_completed;
                },
                ...(!readOnly
                    ? {
                          cta: `${isReceived ? 'Load' : 'Pickup'} items`,
                          callback: ({ order, after }) => {
                              const NOW = new Date();
                              const prompt = (
                                  <>
                                      <Grid
                                          container
                                          direction="row"
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          {isDesktop ? (
                                              <Body1>Check each item as loaded.</Body1>
                                          ) : (
                                              <Header1>{isReceived ? 'Load' : 'Pickup'} items</Header1>
                                          )}
                                      </Grid>
                                  </>
                              );

                              const callback = (orderUpdates, itemTrackingEvents) => {
                                  let events = advance(order, order.event_state, 'CD_COMPLETE', {});

                                  return callbacks.upsertEvent({
                                      events,
                                      orderId: order.order_id,
                                      order: {
                                          loading_completed: NOW.toISOString(),
                                          ...(orderUpdates || {}),
                                      },
                                      trackingEvents: itemTrackingEvents || [],
                                  });
                              };

                              callbacks.openChecklist(
                                  {
                                      title: `${isReceived ? 'Load' : 'Pickup'} Items`,
                                      content: prompt,
                                      documents: [],
                                      signatures: [],
                                      timestamps: [
                                          {
                                              field: 'loading_completed',
                                              name: 'Loading Completed Time',
                                              required: true,
                                          },
                                      ],
                                      // Same fork as the CTA: loaded out of a warehouse, or collected
                                      // straight off the origin having never been in one.
                                      ...(isReceived ? WAREHOUSE_LOAD : ORIGIN_PICKUP),
                                      itemsHelperText: 'Only checked items are recorded.',
                                  },
                                  callback
                              );
                          },
                          secondaryCta: 'Add Exception',
                          secondaryCallback: () => {
                              callbacks.createException({
                                  type: 'PICKUP',
                                  action: null,
                              });
                          },
                      }
                    : {}),
            },
            ...(isReceived
                ? []
                : [
                      {
                          description: `2. Upload proof of pick up documents`,
                          complete: ({ order, after }) => {
                              return !!after && !!order.proof_of_pickup?.length;
                          },
                          ...(!readOnly
                              ? {
                                    cta: `Upload proof of pick up`,
                                    callback: () => {
                                        callbacks.gotoFileTab();
                                    },
                                    secondaryCta: 'Add Exception',
                                    secondaryCallback: () => {
                                        callbacks.createException({
                                            type: 'PICKUP',
                                            action: null,
                                        });
                                    },
                                }
                              : {}),
                      },
                  ]),
            {
                description: `3. Mark order as 'Out for Delivery'`,
                complete: ({ order, after }) => {
                    return !!after?.pickup_date && !!order.ltl_pickup_completed;
                },
                ...(!readOnly
                    ? {
                          cta: `Out for Delivery`,
                          callback: ({ order, after }) => {
                              const NOW = new Date();
                              const prompt = isDesktop ? (
                                  <>
                                      <Grid
                                          direction="row"
                                          container
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          <Body1>Are you sure you want to mark the order as 'Out for Delivery'?</Body1>
                                      </Grid>
                                      <Grid direction="row" container>
                                          <Body1>
                                              This will let the customer and shipper know the order is on the way
                                          </Body1>
                                      </Grid>
                                  </>
                              ) : (
                                  <>
                                      <Grid
                                          direction="row"
                                          container
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          <Header1>{`Out for Delivery`}</Header1>
                                      </Grid>
                                  </>
                              );
                              const callback = (orderUpdates = {}) =>
                                  callbacks.upsertEvent({
                                      event: {
                                          ...(after?.event_id
                                              ? { event_id: after.event_id, action: after.action }
                                              : { action: 'PICKED_UP:CONFIRM' }),
                                          order_id: order.order_id,
                                          pickup_date: orderUpdates?.ltl_pickup_completed || NOW.toISOString(),
                                      },
                                      orderId: order.order_id,
                                      order: {
                                          order_status: 'inProgress',
                                          ltl_pickup_completed: NOW.toISOString(),
                                          ...orderUpdates,
                                      },
                                  });

                              callbacks.openChecklist(
                                  {
                                      title: `Out for Delivery`,
                                      content: prompt,
                                      documents: [],
                                      signatures: [],
                                      timestamps: [
                                          {
                                              field: 'ltl_pickup_completed',
                                              name: 'Out for Delivery Time',
                                              required: true,
                                          },
                                      ],
                                  },
                                  callback
                              );
                          },
                      }
                    : {}),
            },
        ];
        // isReceived selects the CTA labels and the item event, so a stale value records the wrong step.
    }, [isDesktop, full_address, location, location_type, order, isReceived]);

    const isComplete = useMemo(() => {
        return STEPS.map((step) => step.complete({ order, before, after }));
    });

    const overrideComplete = useMemo(() => {
        return (
            !!after &&
            (['inProgress', 'routedInProgress', 'complete', 'routedComplete'].includes(order.order_status) ||
                lastEvent.event_id !== after.event_id)
        );
    }, [before, after, order, lastEvent]);

    const inProgressIdx = useMemo(() => {
        return isComplete.findIndex((x) => !x);
    }, [isComplete]);

    const allComplete = isComplete.every((x) => x);
    const inProgress = !!before && !(allComplete || overrideComplete);

    return (
        <Step
            isDesktop={isDesktop}
            isComplete={overrideComplete || allComplete}
            inProgress={inProgress}
            label={() => {
                return (
                    <Grid
                        direction="row"
                        container
                        css={css`
                            align-items: center;
                        `}
                    >
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            <Header1>{`${isReceived ? 'Loaded' : 'Picked Up'} & Out for Delivery`}</Header1>
                        </Grid>
                        {order.loading_completed ? (
                            <Grid
                                direction="column"
                                css={css`
                                    margin-right: 8px;
                                `}
                            >
                                <Tag>
                                    <Body1
                                        css={css`
                                            margin-right: 20px;
                                        `}
                                    >{`Loaded: ${formatInTimeZone(
                                        order.loading_completed,
                                        tz,
                                        'EEE, MMM dd, yyyy h:mm a zzz'
                                    )}`}</Body1>
                                    {!readOnly && (
                                        <Anchor onClick={() => STEPS[0]?.callback({ order, before, after })}>
                                            <Header1>Edit</Header1>
                                        </Anchor>
                                    )}
                                </Tag>
                            </Grid>
                        ) : null}
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            {after?.pickup_date ? (
                                <Tag>
                                    <Body1
                                        css={css`
                                            margin-right: 20px;
                                        `}
                                    >{`Out for Delivery: ${formatInTimeZone(
                                        after.pickup_date,
                                        tz,
                                        'EEE, MMM dd, yyyy h:mm a zzz'
                                    )}`}</Body1>
                                    {!readOnly && (
                                        <Anchor onClick={() => STEPS[2]?.callback({ order, before, after })}>
                                            <Header1>Edit</Header1>
                                        </Anchor>
                                    )}
                                </Tag>
                            ) : (
                                <Tag>
                                    <Body1>{`Scheduled: ${
                                        before?.est_pickup_date
                                            ? formatCalendarDate(before.est_pickup_date, tz)
                                            : 'Pending'
                                    }`}</Body1>
                                </Tag>
                            )}
                        </Grid>
                    </Grid>
                );
            }}
        >
            {() => (
                <>
                    {STEPS.map(({ description, cta, callback }, idx) => {
                        const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;

                        return (
                            <SubStep
                                key={idx}
                                isComplete={isComplete[idx]}
                                inProgress={inProgressIdx === idx}
                                isLoading={isLoading}
                                isLast={idx === STEPS.length - 1}
                                description={description}
                                cta={cta}
                                secondaryCta={null}
                                callback={
                                    callback
                                        ? () => {
                                              callback({ before, after, order });
                                          }
                                        : null
                                }
                                secondaryCallback={null}
                            />
                        );
                    })}
                </>
            )}
        </Step>
    );
}

export default function PickedUp({ isDesktop, ...rest }) {
    // Service orders swap the 3-step pickup for a single "Out for Service" action.
    if (rest.order?.order_type === 'service') {
        return <OutForService {...rest} isDesktop={isDesktop} />;
    }

    return (
        <>
            <Pickup {...rest} isDesktop={isDesktop} />
        </>
    );
}
